import { useCallback, useEffect, useRef, useState } from 'react';
import type { Message } from '@supporchat/shared';
import { installAudioUnlock, isAudioUnlocked, playMessageChime } from '../lib/audio';
import {
  notificationState,
  notify,
  requestNotificationPermission,
  setTitleBadge,
  type NotificationState,
} from '../lib/notify';

export interface MessageAlerts {
  /** Total de mensagens de cliente ainda não vistas nesta sessão do painel. */
  unseen: number;
  notifications: NotificationState;
  audioReady: boolean;
  /** Pede a permissão de notificação — precisa vir de um clique. */
  enableNotifications: () => Promise<void>;
  /** Zera o contador (o atendente abriu a conversa). */
  clear: () => void;
}

/**
 * Avisa o atendente quando o cliente escreve.
 *
 * Três avisos em camadas, porque cada um falha em uma situação diferente: o som
 * não toca se o navegador ainda não recebeu um gesto, a notificação do sistema
 * depende de permissão, e o título da aba sempre funciona. Juntos, mensagem nova
 * não passa batida.
 *
 * O que NÃO gera aviso: mensagem que o próprio painel enviou, resposta do
 * agente, nota interna do sistema, e mensagem da conversa que já está aberta na
 * tela com a aba em primeiro plano — nesse caso o atendente está lendo.
 */
export function useMessageAlerts(params: {
  messageEvent: { conversationId: string; message: Message } | null;
  /** Conversa aberta na tela agora, se houver. */
  openConversationId: string | null;
}): MessageAlerts {
  const [unseen, setUnseen] = useState(0);
  const [notifications, setNotifications] = useState<NotificationState>(() => notificationState());
  const [audioReady, setAudioReady] = useState(false);

  // Evita avisar duas vezes: o React pode reprocessar o mesmo evento, e o
  // servidor reenvia a mensagem em message.updated quando um áudio é transcrito.
  const alerted = useRef<Set<string>>(new Set());
  const openRef = useRef(openConversationIdOf(params));
  openRef.current = params.openConversationId;

  useEffect(() => {
    installAudioUnlock();

    // O desbloqueio acontece no primeiro gesto; refletir isso na interface
    // permite avisar o atendente de que o som ainda está mudo.
    const check = () => setAudioReady(isAudioUnlocked());
    const timer = window.setInterval(check, 1000);
    check();

    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const event = params.messageEvent;
    if (!event) return;

    const { message } = event;

    // Só mensagem escrita pelo cliente interessa como aviso.
    if (message.direction !== 'inbound' || message.sender !== 'contact') return;
    if (alerted.current.has(message.id)) return;
    alerted.current.add(message.id);

    const hidden = document.visibilityState === 'hidden';
    const isOpenConversation = openRef.current === event.conversationId;

    // Conversa aberta e aba em foco: o atendente está vendo chegar.
    if (isOpenConversation && !hidden) return;

    setUnseen((current) => current + 1);
    playMessageChime();

    if (hidden) {
      const who = message.senderName ?? 'Cliente';
      notify({
        title: `Nova mensagem de ${who}`,
        body: previewOf(message),
        // Uma notificação por conversa: cinco mensagens seguidas do mesmo
        // cliente atualizam o mesmo aviso em vez de empilhar cinco.
        tag: `conversa-${event.conversationId}`,
      });
    }
  }, [params.messageEvent]);

  useEffect(() => {
    setTitleBadge(unseen);
    return () => setTitleBadge(0);
  }, [unseen]);

  const enableNotifications = useCallback(async () => {
    setNotifications(await requestNotificationPermission());
  }, []);

  const clear = useCallback(() => setUnseen(0), []);

  return { unseen, notifications, audioReady, enableNotifications, clear };
}

function openConversationIdOf(params: { openConversationId: string | null }): string | null {
  return params.openConversationId;
}

function previewOf(message: Message): string {
  if (message.type === 'audio') {
    return message.transcriptionStatus === 'done' ? message.content : '🎤 Áudio recebido';
  }
  if (message.type !== 'text') return `Enviou ${message.type}`;

  const text = message.content.replace(/\s+/g, ' ').trim();
  return text.length > 120 ? `${text.slice(0, 117)}...` : text || 'Mensagem recebida';
}
